import { ValidatedForm } from '@fv/client-components'
import { useFormModelState } from '@fv/client-core'
import { fvDate, fvDateUtc } from '@fv/dates'
import {
  systemNotificationTypes,
  type UpsertSystemNotificationDTO,
  type UserType,
} from '@fv/models'
import { toggleArrayItem } from '@fv/models/core'

import { AdminButton } from '@/components/shared/AdminButton'
import { AdminFormGroup } from '@/components/shared/AdminFormGroup'
import { AdminSelect } from '@/components/shared/AdminSelect'
import { AdminTextArea } from '@/components/shared/AdminTextArea'
import { AdminToggle } from '@/components/shared/AdminToggle'
import { useCarriers } from '@/hooks/useCarriers'

import { useCreateNotification, useUpdateNotification } from './mutations'
import type { NotificationFormModel } from './types'

type Props = {
  onClose?: () => void
  initialValue?: NotificationFormModel
}
const dateInputFormat = 'YYYY-MM-DDTHH:mm'
const options = [...systemNotificationTypes]
const userTypes: UserType[] = ['shipper', 'vendor', 'carrier']

export const NotificationForm = (props: Props) => {
  const { onClose, initialValue } = props
  const carriersOptions = [{ name: 'All', id: '' }, ...useCarriers()]
  const { register, value, setValue } =
    useFormModelState<NotificationFormModel>({
      initialValue: initialValue
        ? {
            ...initialValue,
            expiration: fvDateUtc(initialValue.expiration)
              .local()
              .format(dateInputFormat),
          }
        : {
            type: 'warning',
            expiration: fvDate().add(1, 'day').format(dateInputFormat),
            text: '',
            _id: '',
            userType: ['shipper'],
          },
    })
  const createSystemNotification = useCreateNotification()
  const updateNotification = useUpdateNotification()

  const isSaving = createSystemNotification.isPending

  const handleSubmit = async () => {
    if (!value.expiration) return
    const body: UpsertSystemNotificationDTO = {
      ...value,
      carrierId: value.carrierId || undefined,
      userType: value.userType || undefined,
      expiration: fvDate(value.expiration).utc().toDate().toISOString(),
    }
    if (initialValue?._id) {
      await updateNotification.mutateAsync({ id: initialValue._id, body })
    } else {
      await createSystemNotification.mutateAsync(body)
    }
    onClose?.()
  }
  return (
    <ValidatedForm onValidSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <AdminFormGroup label="Type">
          <AdminSelect
            options={options}
            className="w-full"
            name="type"
            required
            {...register('type')}
          />
        </AdminFormGroup>
        <AdminFormGroup label="Expiration">
          <input
            type="datetime-local"
            name="expiration"
            className="input"
            required
            {...register('expiration')}
          />
        </AdminFormGroup>
      </div>
      <div>
        <AdminFormGroup label="carrier">
          <AdminSelect
            options={
              carriersOptions?.map(c => ({
                text: c?.name || '',
                value: c?.id || '',
              })) || []
            }
            className="w-full"
            name="carrier"
            required
            {...register('carrierId')}
          />
        </AdminFormGroup>
      </div>
      <div>
        <AdminFormGroup label="User Groups">
          <div className="grid grid-cols-4 md:grid-cols-6 gap-x-4">
            {userTypes.map(type => (
              <AdminToggle
                label={type}
                id={type}
                key={type}
                name={type}
                checked={value.userType?.includes(type)}
                onChange={e =>
                  setValue({
                    ...value,
                    userType: toggleArrayItem(
                      value.userType,
                      type,
                      e.target.checked,
                    ),
                  })
                }
              />
            ))}
          </div>
        </AdminFormGroup>
      </div>
      <div>
        <AdminFormGroup label="Text">
          <AdminTextArea
            required
            value={value.text}
            onChange={e => setValue(p => ({ ...p, text: e.target.value }))}
          />
        </AdminFormGroup>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <AdminButton className="btn-error" onClick={onClose} loading={isSaving}>
          Cancel
        </AdminButton>
        <AdminButton className="btn-primary" type="submit" loading={isSaving}>
          Save
        </AdminButton>
      </div>
    </ValidatedForm>
  )
}
